import {
  Alert,
  CircularProgress,
  Grid,
} from '@legacy/components/mui-components';
import classNames from 'classnames';
import { useGetReportWidgetAll } from '@legacy/apis/reportingWidgetsAPI';
import { AverageHourlyRateReportingWidget } from '../AverageHourlyRateReportingWidget';
import { InvoicedPerMonthReportingWidget } from '../InvoicedPerMonthReportingWidget';
import { OverdueInvoicesReportingWidget } from '../OverdueInvoicesReportingWidget';
import { PlannedAbsenceReportingWidget } from '../PlannedAbsenceReportingWidget';
import { ProjectEconomyHealthReportingWidget } from '../ProjectEconomyHealthReportingWidget';
import { ReportingWidgetControl } from './components/ReportingWidgetControl';
import { TimeTrackingDelayReportingWidget } from '../TimeTrackingDelayReportingWidget';
import { VacationCalculationReportingWidget } from '../VacationCalculationReportingWidget';
import { BillablePercentageWidget } from '../BillablePercentageWidget';
import { ExternalPercentageWidget } from '../ExternalPercentageWidget';
import {
  AddedValuePerMonthReportingWidget,
  AddedValueBarPerMonthReportingWidget,
  AddedValuePerMonthHoursOnlyReportingWidget,
} from '../AddedValuePerMonthReportingWidget';
import styles from './ReportingWidgetContainer.module.scss';
import { PersonalPlanWidget } from '../PersonalPlanWidget';
import { useTranslation } from 'react-i18next';

type WidgetColumns = {
  xs: number;
  md: number;
  lg: number;
};

// Default column configuration
const FULL_COLUMNS: WidgetColumns = { xs: 12, md: 12, lg: 12 };
const HALF_COLUMNS: WidgetColumns = { xs: 12, md: 6, lg: 6 };

const HALF_COLUMN_WIDGETS = new Set([
  'TimeTrackingDelay',
  'BillablePercentage',
  'ExternalPercentage',
]);

// Widget component mapping
const WIDGET_COMPONENTS: Record<string, React.ComponentType> = {
  PersonalPlan: PersonalPlanWidget,
  HourKPIHoursOnly: AddedValuePerMonthHoursOnlyReportingWidget,
  HourKPI: AddedValuePerMonthReportingWidget,
  HourKPIWithBAR: AddedValueBarPerMonthReportingWidget,
  InvoicedPerMonth: InvoicedPerMonthReportingWidget,
  AverageHourlyRate: AverageHourlyRateReportingWidget,
  PlannedAbsence: PlannedAbsenceReportingWidget,
  ProjectEconomyHealth: ProjectEconomyHealthReportingWidget,
  OverdueInvoices: OverdueInvoicesReportingWidget,
  TimeTrackingDelay: TimeTrackingDelayReportingWidget,
  VacationCalculation: VacationCalculationReportingWidget,
  BillablePercentage: BillablePercentageWidget,
  ExternalPercentage: ExternalPercentageWidget,
};

export const ReportingWidgetContainer = () => {
  const { widgetList, isError, isLoading } = useGetReportWidgetAll();
  const { t } = useTranslation('reportingWidgets');

  if (isLoading) {
    return (
      <div className={classNames(styles.widgetContainer)}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <CircularProgress />
          </Grid>
        </Grid>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={classNames(styles.widgetContainer)}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Alert severity="error">{t('WidgetsFailedToLoadText')}</Alert>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <div
      data-automation-id="ReportWidgetContainer"
      className={classNames(styles.widgetContainer)}
    >
      <Grid container>
        {widgetList && widgetList?.length > 0 ? (
          widgetList?.map((widget, index) => {
            const Component = WIDGET_COMPONENTS[widget.type];
            if (!Component) return null;

            const cols = HALF_COLUMN_WIDGETS.has(widget.type)
              ? HALF_COLUMNS
              : FULL_COLUMNS;

            return (
              <ReportingWidgetControl
                key={`${widget.type}-${index}`}
                widgetTypeName={widget.type}
              >
                <Grid size={cols}>
                  <Component />
                </Grid>
              </ReportingWidgetControl>
            );
          })
        ) : (
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">{t('NoWidgetsAvailableText')}</Alert>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default ReportingWidgetContainer;
